import { useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { ApiError } from "../lib/api";
import { Button, cn } from "../components/ui";
import { Icon } from "../lib/icons";
import { greeting } from "../lib/decor";

const FEATURES = [
  { icon: "box", text: "Единый остаток со Sheets и Power BI" },
  { icon: "scale", text: "Сверка расхождений в реальном времени" },
  { icon: "calendarRange", text: "Отчёты по дням, неделям и месяцам" },
];

export function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login(username, password);
      navigate(params.get("next") || "/dashboard", { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Ошибка входа");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen grid-cols-1 bg-bg lg:grid-cols-[1.1fr_1fr]">
      {/* Brand panel — hidden on phones. 2026-09-28 minimalist pass: the animated gradient/crate
          scene became a quiet surface with a faint dot grid; same copy and feature list. */}
      <div className="relative hidden overflow-hidden border-r border-border bg-surface lg:flex lg:flex-col lg:justify-between lg:p-12">
        <div
          className="pointer-events-none absolute inset-0 opacity-60"
          style={{
            backgroundImage: "radial-gradient(rgb(var(--border-strong)) 1px, transparent 1px)",
            backgroundSize: "22px 22px",
            maskImage: "linear-gradient(180deg, transparent, black 30%, black 70%, transparent)",
            WebkitMaskImage: "linear-gradient(180deg, transparent, black 30%, black 70%, transparent)",
          }}
        />

        <div className="relative flex items-center gap-2.5 text-md font-semibold tracking-tight">
          <span className="grid h-8 w-8 flex-none place-items-center rounded-lg bg-accent text-xs font-bold text-white">СА</span>
          Складская аналитика
        </div>

        <div className="relative max-w-md">
          <div className="text-3xl font-semibold leading-tight tracking-tight">
            Остатки, поставки и отгрузки — в одном месте.
          </div>
          <ul className="mt-7 flex flex-col gap-3.5">
            {FEATURES.map((f) => (
              <li key={f.text} className="flex items-center gap-3 text-base text-text-2">
                <span className="grid h-8 w-8 flex-none place-items-center rounded-lg border border-border bg-surface text-text">
                  <Icon name={f.icon} size={16} />
                </span>
                {f.text}
              </li>
            ))}
          </ul>
        </div>

        <div className="relative text-xs text-muted">© {new Date().getFullYear()} Складская аналитика</div>
      </div>

      <div className="grid place-items-center px-4 py-10">
        <div className="w-full max-w-[360px]">
          <form onSubmit={submit} className="animate-fade-in">
            <div className="mb-8 flex items-center gap-2 text-base font-semibold tracking-tight lg:hidden">
              <span className="grid h-7 w-7 flex-none place-items-center rounded-lg bg-accent text-2xs font-bold text-white">СА</span>
              Складская аналитика
            </div>
            <h1 className="text-xl font-semibold tracking-tight">{greeting()}</h1>
            <p className="mb-7 mt-1.5 text-base text-text-2">Логин и пароль выдаёт администратор.</p>

            <label className="mb-1.5 block text-sm font-medium text-text">Логин</label>
            <div className="relative mb-4">
              <Icon name="user" size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
              <input
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoFocus
                disabled={busy}
                autoComplete="username"
                aria-invalid={!!error}
              className={cn("w-full rounded-lg border bg-surface py-2.5 pl-9 pr-3 text-base focus:shadow-[0_0_0_3px_rgb(var(--accent)/0.12)] focus:outline-none disabled:opacity-60", error ? "border-danger/60" : "border-border focus:border-accent/60")}
              />
            </div>

            <label className="mb-1.5 block text-sm font-medium text-text">Пароль</label>
            <div className="relative mb-6">
              <Icon name="lock" size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
              <input
                type={showPw ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                aria-invalid={!!error}
              className={cn("w-full rounded-lg border bg-surface py-2.5 pl-9 pr-9 text-base focus:shadow-[0_0_0_3px_rgb(var(--accent)/0.12)] focus:outline-none disabled:opacity-60", error ? "border-danger/60" : "border-border focus:border-accent/60")}
              />
              <button
                type="button"
                onClick={() => setShowPw((v) => !v)}
                tabIndex={-1}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted hover:text-text-2"
                title={showPw ? "Скрыть пароль" : "Показать пароль"}
              >
                <Icon name={showPw ? "eyeOff" : "eye"} size={16} />
              </button>
            </div>

            {error && (
              <div className="mb-4 flex items-center gap-2 rounded-lg bg-danger-weak px-3 py-2 text-sm font-medium text-danger">
                <Icon name="alert" size={14} className="flex-none" />
                {error}
              </div>
            )}

            <Button
              type="submit"
              variant="primary"
              loading={busy}
              className="group min-h-10 w-full justify-center text-base"
            >
              Войти
              {!busy && <Icon name="arrowRight" size={16} className="transition-transform group-hover:translate-x-0.5" />}
            </Button>
          </form>

          <div className="mt-6 flex items-center justify-center gap-1.5 text-xs text-muted">
            <Icon name="shield" size={14} />
            Доступ только для сотрудников склада
          </div>
        </div>
      </div>
    </div>
  );
}
